import React, { useState, Fragment } from 'react';
import { StatusChip } from '../ui/StatusChip';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface Client {
  id: string;
  nome: string;
  piva: string;
  piani: {
    id: string;
    nomePiano: string;
    stato: 'draft' | 'pending' | 'active' | 'paused' | 'canceled' | 'completed';
    importoTotale: number;
    importoSaldato: number;
    importoRimanente: number;
    ratePagate: number;
    rateDaPagare: number;
    rateSaltate: number;
    dataCreazione: string;
    dataScadenza: string;
  }[];
}
interface ClientPaymentPlanViewProps {
  searchTerm: string;
  dateRange: {
    start: Date | null;
    end: Date | null;
    preset?: string;
  };
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
}
export function ClientPaymentPlanView({
  searchTerm,
  dateRange,
  sortField,
  sortDirection,
  onSort
}: ClientPaymentPlanViewProps) {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  // Dati fittizi per la tabella
  const clienti: Client[] = [{
    id: '1',
    nome: 'Acme Srl',
    piva: '12345678901',
    piani: [{
      id: 'plan-1',
      nomePiano: 'Piano rientro Q1 2023',
      stato: 'active',
      importoTotale: 12500,
      importoSaldato: 5000,
      importoRimanente: 7500,
      ratePagate: 2,
      rateDaPagare: 3,
      rateSaltate: 0,
      dataCreazione: '2023-04-15',
      dataScadenza: '2023-09-15'
    }, {
      id: 'plan-2',
      nomePiano: 'Piano rientro Q2 2023',
      stato: 'draft',
      importoTotale: 4800,
      importoSaldato: 0,
      importoRimanente: 4800,
      ratePagate: 0,
      rateDaPagare: 6,
      rateSaltate: 0,
      dataCreazione: '2023-05-20',
      dataScadenza: '2023-11-20'
    }]
  }, {
    id: '2',
    nome: 'TechSolutions Spa',
    piva: '98765432101',
    piani: [{
      id: 'plan-3',
      nomePiano: 'Piano rientro Annuale 2023',
      stato: 'pending',
      importoTotale: 35000,
      importoSaldato: 0,
      importoRimanente: 35000,
      ratePagate: 0,
      rateDaPagare: 10,
      rateSaltate: 0,
      dataCreazione: '2023-05-10',
      dataScadenza: '2024-03-10'
    }]
  }, {
    id: '3',
    nome: 'Industria Meccanica Srl',
    piva: '45678912301',
    piani: [{
      id: 'plan-4',
      nomePiano: 'Piano rientro Marzo-Agosto',
      stato: 'active',
      importoTotale: 18000,
      importoSaldato: 6000,
      importoRimanente: 12000,
      ratePagate: 2,
      rateDaPagare: 4,
      rateSaltate: 1,
      dataCreazione: '2023-03-05',
      dataScadenza: '2023-09-05'
    }]
  }, {
    id: '4',
    nome: 'Logistica Express Srl',
    piva: '78901234561',
    piani: [{
      id: 'plan-5',
      nomePiano: 'Piano rientro Semestrale',
      stato: 'active',
      importoTotale: 24000,
      importoSaldato: 8000,
      importoRimanente: 16000,
      ratePagate: 2,
      rateDaPagare: 4,
      rateSaltate: 0,
      dataCreazione: '2023-02-15',
      dataScadenza: '2023-08-15'
    }, {
      id: 'plan-6',
      nomePiano: 'Piano rientro Precedente',
      stato: 'completed',
      importoTotale: 9600,
      importoSaldato: 9600,
      importoRimanente: 0,
      ratePagate: 6,
      rateDaPagare: 0,
      rateSaltate: 0,
      dataCreazione: '2022-08-10',
      dataScadenza: '2023-02-10'
    }]
  }, {
    id: '5',
    nome: 'Alimentari Freschi Spa',
    piva: '56789012341',
    piani: [{
      id: 'plan-7',
      nomePiano: 'Piano rientro Urgente',
      stato: 'canceled',
      importoTotale: 7500,
      importoSaldato: 2500,
      importoRimanente: 5000,
      ratePagate: 1,
      rateDaPagare: 0,
      rateSaltate: 2,
      dataCreazione: '2023-01-20',
      dataScadenza: '2023-04-20'
    }]
  }];
  // Filtraggio per termine di ricerca
  const filteredClienti = clienti.filter(cliente => {
    const matchesSearch = cliente.nome.toLowerCase().includes(searchTerm.toLowerCase()) || cliente.piva.includes(searchTerm) || cliente.piani.some(piano => piano.stato.includes(searchTerm.toLowerCase()) || piano.nomePiano.toLowerCase().includes(searchTerm.toLowerCase()) || piano.importoTotale.toString().includes(searchTerm) || piano.importoSaldato.toString().includes(searchTerm) || piano.importoRimanente.toString().includes(searchTerm));
    // Filtraggio per data
    const matchesDateRange = !dateRange.start || !dateRange.end || cliente.piani.some(() => {
      const pianoDate = new Date(cliente.piani[0].dataCreazione);
      return pianoDate >= dateRange.start! && pianoDate <= dateRange.end!;
    });
    return matchesSearch && matchesDateRange;
  });
  // Ordinamento dei clienti
  const sortedClienti = [...filteredClienti].sort((a, b) => {
    if (sortField === 'nome') {
      return sortDirection === 'asc' ? a.nome.localeCompare(b.nome) : b.nome.localeCompare(a.nome);
    }
    if (sortField === 'pianiAttivi') {
      const aPianiAttivi = a.piani.filter(p => p.stato === 'active' || p.stato === 'pending').length;
      const bPianiAttivi = b.piani.filter(p => p.stato === 'active' || p.stato === 'pending').length;
      return sortDirection === 'asc' ? aPianiAttivi - bPianiAttivi : bPianiAttivi - aPianiAttivi;
    }
    if (sortField === 'importoTotale') {
      const aImportoTotale = a.piani.reduce((sum, piano) => sum + piano.importoTotale, 0);
      const bImportoTotale = b.piani.reduce((sum, piano) => sum + piano.importoTotale, 0);
      return sortDirection === 'asc' ? aImportoTotale - bImportoTotale : bImportoTotale - aImportoTotale;
    }
    if (sortField === 'importoRimanente') {
      const aImportoRimanente = a.piani.reduce((sum, piano) => sum + piano.importoRimanente, 0);
      const bImportoRimanente = b.piani.reduce((sum, piano) => sum + piano.importoRimanente, 0);
      return sortDirection === 'asc' ? aImportoRimanente - bImportoRimanente : bImportoRimanente - aImportoRimanente;
    }
    return 0;
  });
  // Funzione per gestire l'espansione delle righe
  const toggleRowExpanded = (id: string) => {
    setExpandedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
  };
  // Formattazione dell'importo in euro
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };
  // Mapping dello stato per il componente StatusChip
  const mapStatoToStatusType = (stato: string): any => {
    const mapping: Record<string, any> = {
      draft: 'draft',
      pending: 'pending',
      active: 'active',
      paused: 'paused',
      canceled: 'canceled',
      completed: 'paid'
    };
    return mapping[stato] || 'draft';
  };
  // Etichette per gli stati
  const statoLabels: Record<string, string> = {
    draft: 'Bozza',
    pending: 'In Firma',
    active: 'Firmato',
    paused: 'In Pausa',
    canceled: 'Annullato',
    completed: 'Completato'
  };
  // Renderizza l'icona di ordinamento
  const renderSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-909" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-910" />;
  };
  // Calcola il totale dei piani attivi per un cliente
  const getPianiAttivi = (cliente: Client) => {
    return cliente.piani.filter(p => p.stato === 'active' || p.stato === 'pending').length;
  };
  // Calcola l'importo totale di tutti i piani di un cliente
  const getImportoTotale = (cliente: Client) => {
    return cliente.piani.reduce((sum, piano) => sum + piano.importoTotale, 0);
  };
  // Calcola l'importo rimanente di tutti i piani di un cliente
  const getImportoRimanente = (cliente: Client) => {
    return cliente.piani.reduce((sum, piano) => sum + piano.importoRimanente, 0);
  };
  return <div className="overflow-x-auto" data-id="element-911">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-912">
        <thead className="bg-gray-50" data-id="element-913">
          <tr data-id="element-914">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('nome')} data-id="element-915">
              <div className="flex items-center" data-id="element-916">
                Cliente
                {renderSortIcon('nome')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('pianiAttivi')} data-id="element-917">
              <div className="flex items-center" data-id="element-918">
                Piani Attivi
                {renderSortIcon('pianiAttivi')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('importoTotale')} data-id="element-919">
              <div className="flex items-center" data-id="element-920">
                Importo Totale
                {renderSortIcon('importoTotale')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('importoRimanente')} data-id="element-921">
              <div className="flex items-center" data-id="element-922">
                Importo Rimanente
                {renderSortIcon('importoRimanente')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-923">
              Dettagli
            </th>
            <th scope="col" className="relative px-6 py-3" data-id="element-924">
              <span className="sr-only" data-id="element-925">Azioni</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-926">
          {sortedClienti.length === 0 ? <tr data-id="element-927">
              <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-928">
                Nessun cliente trovato
              </td>
            </tr> : sortedClienti.map(cliente => <Fragment key={cliente.id} data-id="element-929">
                <tr className="hover:bg-gray-50" data-id="element-930">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-931">
                    <div className="text-sm font-medium text-gray-900" data-id="element-932">
                      {cliente.nome}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-933">{cliente.piva}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-934">
                    <div className="text-sm font-medium text-gray-900" data-id="element-935">
                      {getPianiAttivi(cliente)} / {cliente.piani.length}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-936">
                    <div className="text-sm font-medium text-gray-900" data-id="element-937">
                      {formatCurrency(getImportoTotale(cliente))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-938">
                    <div className="text-sm text-red-600 font-medium" data-id="element-939">
                      {formatCurrency(getImportoRimanente(cliente))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-940">
                    <button type="button" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-900" onClick={() => toggleRowExpanded(cliente.id)} data-id="element-941">
                      Vedi piani
                      {expandedRows.includes(cliente.id) ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-942" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-943" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-944">
                    <a href="#" className="text-blue-600 hover:text-blue-900" data-id="element-945">
                      Profilo
                    </a>
                  </td>
                </tr>
                {expandedRows.includes(cliente.id) && <tr data-id="element-946">
                    <td colSpan={6} className="px-6 py-4 bg-gray-50" data-id="element-947">
                      <div className="text-sm text-gray-900 font-medium mb-2" data-id="element-948">
                        Piani di Rientro
                      </div>
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-949">
                        <thead className="bg-gray-100" data-id="element-950">
                          <tr data-id="element-951">
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-952">
                              Nome Piano
                            </th>
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-953">
                              Stato
                            </th>
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-954">
                              Importo
                            </th>
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-955">
                              Rimanente
                            </th>
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-956">
                              Rate
                            </th>
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-957">
                              Data Creazione
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-958">
                          {cliente.piani.map(piano => <tr key={piano.id} className="hover:bg-gray-50" data-id="element-959">
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-960">
                                {piano.nomePiano}
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap" data-id="element-961">
                                <StatusChip status={mapStatoToStatusType(piano.stato)} label={statoLabels[piano.stato]} data-id="element-962" />
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-963">
                                {formatCurrency(piano.importoTotale)}
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-red-600" data-id="element-964">
                                {formatCurrency(piano.importoRimanente)}
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-965">
                                {piano.ratePagate} pagate, {piano.rateDaPagare}{' '}
                                da pagare
                                {piano.rateSaltate > 0 && <span className="text-red-600" data-id="element-966">
                                    , {piano.rateSaltate} saltate
                                  </span>}
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-967">
                                {new Date(piano.dataCreazione).toLocaleDateString('it-IT')}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </td>
                  </tr>}
              </Fragment>)}
        </tbody>
      </table>
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-968">
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-969">
          <div data-id="element-970">
            <p className="text-sm text-gray-700" data-id="element-971">
              Visualizzazione{' '}
              <span className="font-medium" data-id="element-972">{sortedClienti.length}</span> di{' '}
              <span className="font-medium" data-id="element-973">{clienti.length}</span> clienti
            </p>
          </div>
          <div data-id="element-974">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-975">
              <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-976">
                <span className="sr-only" data-id="element-977">Precedente</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-978">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-979" />
                </svg>
              </button>
              <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-980">
                1
              </button>
              <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-981">
                <span className="sr-only" data-id="element-982">Successivo</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-983">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" data-id="element-984" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>;
}